import React from 'react';
import { MessageCircle } from 'lucide-react';
import { MessageBubble } from './MessageBubble';
import { DateSeparator } from './DateSeparator';
import { ConversionLine } from './ConversionLine';
import type { Message } from '../hooks/useConversation';

interface MessageListProps {
  messages: Message[];
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onReply?: (msg: Message) => void;
}

const GROUP_GAP_MS = 60 * 1000; // 60s — group messages from same sender within 1min

export function MessageList({ messages, messagesEndRef, onReply }: MessageListProps) {
  if (messages.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto wa-wallpaper flex flex-col items-center justify-center text-center p-4">
        <div className="bg-wa-in/90 px-4 py-3 rounded-lg shadow-sm">
          <MessageCircle className="w-10 h-10 text-wa-text-muted mx-auto mb-2" />
          <p className="text-wa-text font-medium">Nenhuma mensagem</p>
          <p className="text-sm text-wa-text-muted">Envie sua primeira mensagem abaixo</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto overflow-x-hidden px-[5%] py-3 wa-wallpaper scrollbar-hide">
      {messages.map((msg, index) => {
        const timestamp = msg.sent_at || msg.received_at || msg.created_at;
        const prev = index > 0 ? messages[index - 1] : null;
        const prevTimestamp = prev
          ? prev.sent_at || prev.received_at || prev.created_at
          : null;

        const showDate = index === 0 || (
          prevTimestamp && new Date(prevTimestamp).toDateString() !== new Date(timestamp).toDateString()
        );

        const showConversion = index > 0 && messages[index - 1].wasLead && !msg.wasLead;

        const grouped = !showDate && !showConversion && prev
          && prev.direction === msg.direction
          && prevTimestamp
          && (new Date(timestamp).getTime() - new Date(prevTimestamp).getTime()) < GROUP_GAP_MS;

        return (
          <React.Fragment key={msg.id}>
            {showDate && <DateSeparator timestamp={timestamp} />}
            {showConversion && <ConversionLine />}
            <MessageBubble
              message={msg}
              grouped={!!grouped}
              showTail={!grouped}
              onReply={onReply}
            />
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}

